import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { DcBaseRequester } from '../core/dc-base.requester';
import { IConnector, IConnectorResponse } from './connector.model';

@Injectable({
	providedIn: 'root',
})
export class ConnectorRequester extends DcBaseRequester<IConnectorResponse> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;
	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('CONNECTORS');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getAvailableConnectors(
		excludedTypes: Array<string>,
		excludedModes: Array<string>
	): Observable<Array<IConnector>> {
		const url = this.urlService.generateUrl(
			`${this.resource}`,
			{},
			{
				details: true,
				project: this.projectId,
				excludedTypes,
				excludedModes,
			}
		);
		return this.get<Array<IConnector>>(url);
	}

	public getConnectorList(
		useCache = true
	): Observable<ReadonlyArray<IConnectorResponse>> {
		const resourceUrl = this.generateResourceUrl('CONNECTORS_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				details: true,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IConnectorResponse>>(url, cachePolicy);
	}

	public deleteConnector(connectorId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('CONNECTORS_V2_PRIVATE_BY_ID');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				connectorId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public getConnector(connectorId: number): Observable<IConnector> {
		const resourceUrl = this.generateResourceUrl('CONNECTORS_V2_BY_ID');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				connectorId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IConnector>(url);
	}

	public updateConnector(connector: IConnector): Observable<void> {
		const resourceUrl = this.generateResourceUrl('CONNECTORS_V2_PRIVATE_BY_ID');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				connectorId: connector.id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, connector);
	}

	public deleteMany(connectorIds: Array<number>): Observable<void> {
		const url = this.urlService.generateUrl(
			this.resource,
			{},
			{
				project: this.projectId,
				connectorIds,
			}
		);
		return this.http.delete<void>(url);
	}
}
